import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  GripVertical,
  Image as ImageIcon,
  TrendingUp,
  Clock,
  Package,
  MessageSquare,
  Type,
  Send,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getNewsletterProductsTrending,
  getNewsletterProductsLatest,
  uploadNewsletterImage,
} from "../../../config/api";
import { API_BASE_URL } from "../../../config/api";

const SECTION_TYPES = [
  { id: "trending", label: "Top trending", icon: TrendingUp },
  { id: "latest", label: "Latest arrivals", icon: Clock },
  { id: "hand_picked", label: "Hand-picked products", icon: Package },
  { id: "testimonials", label: "What others say", icon: MessageSquare },
  { id: "custom_text", label: "Custom text / HTML", icon: Type },
];

const NewsletterBuilder = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [headerImageUrl, setHeaderImageUrl] = useState(null);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [sections, setSections] = useState([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerForSection, setProductPickerForSection] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);

  const baseUrl = API_BASE_URL?.replace(/\/api\/?$/, "") || "";

  const handleHeaderImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    setHeaderUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await uploadNewsletterImage(formData);
      setHeaderImageUrl(res?.data?.url || res?.url || null);
      toast.success("Header image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setHeaderUploading(false);
    }
  };

  const addSection = (type) => {
    setAddSectionOpen(false);
    const config = {};
    if (type === "hand_picked") config.productIds = [];
    if (type === "testimonials") config.quotes = [{ name: "", text: "", rating: 5 }];
    if (type === "custom_text") config.html = "";
    if (type === "trending") config.title = "Top trending";
    if (type === "latest") config.title = "Latest arrivals";
    setSections((prev) => [...prev, { type, config, id: Date.now().toString() }]);
  };

  const removeSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSectionConfig = (id, key, value) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, config: { ...s.config, [key]: value } } : s
      )
    );
  };

  const updateSectionQuotes = (id, quotes) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, config: { ...s.config, quotes } } : s))
    );
  };

  const openProductPicker = (sectionId) => {
    setProductPickerForSection(sectionId);
    setProductPickerOpen(true);
    setProductsLoading(true);
    getNewsletterProductsLatest(100)
      .then((res) => setProducts(res?.data?.products || []))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setProductsLoading(false));
  };

  const toggleProductInSection = (sectionId, productId) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        const ids = s.config?.productIds || [];
        const next = ids.includes(productId)
          ? ids.filter((x) => x !== productId)
          : [...ids, productId];
        return { ...s, config: { ...s.config, productIds: next } };
      })
    );
  };

  const buildPayload = useCallback(() => {
    return {
      subject: subject || "Newsletter from Knots & Krafts",
      headerImageUrl: headerImageUrl || null,
      sections: sections.map(({ type, config }) => ({ type, config })),
    };
  }, [subject, headerImageUrl, sections]);

  const handlePreviewAndSend = () => {
    const payload = buildPayload();
    navigate("/admin/newsletter/preview", { state: { payload } });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Newsletter</h1>
        <p className="mt-1 text-sm text-gray-600">
          Build your newsletter, then preview and send to customers
        </p>
      </div>

      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject line
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. New arrivals & top picks this week"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <ImageIcon className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Header image (optional)</span>
          </div>
          {headerImageUrl ? (
            <div className="flex items-center gap-4">
              <img
                src={headerImageUrl.startsWith("http") ? headerImageUrl : `${baseUrl}${headerImageUrl}`}
                alt="Header"
                className="h-24 object-cover rounded border"
              />
              <div className="flex gap-2">
                <label className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                  Change
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={handleHeaderImageChange}
                    disabled={headerUploading}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setHeaderImageUrl(null)}
                  className="inline-flex items-center px-3 py-1.5 border border-red-200 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <span className="text-sm text-gray-500">
                {headerUploading ? "Uploading…" : "Upload header image (max 2MB)"}
              </span>
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleHeaderImageChange}
                disabled={headerUploading}
              />
            </label>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-700">Sections</span>
            <div className="relative">
              <button
                type="button"
                onClick={() => setAddSectionOpen((v) => !v)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-1" /> Add section
              </button>
              {addSectionOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setAddSectionOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1">
                      {SECTION_TYPES.map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => addSection(id)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Icon className="w-4 h-4 mr-2 text-gray-500" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <ul className="space-y-3">
            {sections.map((section) => {
              const meta = SECTION_TYPES.find((t) => t.id === section.type);
              const Icon = meta?.icon || Type;
              return (
                <li
                  key={section.id}
                  className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg"
                >
                  <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">
                        {meta?.label || section.type}
                      </span>
                    </div>
                    {section.type === "hand_picked" && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {(section.config?.productIds || []).length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => openProductPicker(section.id)}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          Choose products
                        </button>
                      </div>
                    )}
                    {section.type === "testimonials" && (
                      <div className="space-y-2">
                        {(section.config?.quotes || []).map((q, i) => (
                          <div key={i} className="flex gap-2 text-sm">
                            <input
                              type="text"
                              placeholder="Name"
                              value={q.name}
                              onChange={(e) => {
                                const next = [...(section.config?.quotes || [])];
                                next[i] = { ...next[i], name: e.target.value };
                                updateSectionQuotes(section.id, next);
                              }}
                              className="flex-1 rounded border border-gray-300 px-2 py-1"
                            />
                            <input
                              type="text"
                              placeholder="Quote"
                              value={q.text}
                              onChange={(e) => {
                                const next = [...(section.config?.quotes || [])];
                                next[i] = { ...next[i], text: e.target.value };
                                updateSectionQuotes(section.id, next);
                              }}
                              className="flex-1 rounded border border-gray-300 px-2 py-1"
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            updateSectionQuotes(section.id, [
                              ...(section.config?.quotes || []),
                              { name: "", text: "", rating: 5 },
                            ])
                          }
                          className="text-xs text-primary-600 hover:underline"
                        >
                          + Add quote
                        </button>
                      </div>
                    )}
                    {section.type === "custom_text" && (
                      <textarea
                        placeholder="HTML or plain text"
                        value={section.config?.html || ""}
                        onChange={(e) =>
                          updateSectionConfig(section.id, "html", e.target.value)
                        }
                        rows={3}
                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSection(section.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    aria-label="Remove section"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
          {sections.length === 0 && (
            <p className="text-sm text-gray-500 py-4">
              Add at least one section (e.g. Top trending or Latest arrivals)
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handlePreviewAndSend}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Send className="w-4 h-4 mr-2" />
            Preview &amp; Send
          </button>
        </div>
      </div>

      {productPickerOpen && productPickerForSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Choose products</h3>
              <button
                type="button"
                onClick={() => {
                  setProductPickerOpen(false);
                  setProductPickerForSection(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-auto flex-1">
              {productsLoading ? (
                <p className="text-sm text-gray-500">Loading products…</p>
              ) : (
                <ul className="space-y-2">
                  {products.map((p) => {
                    const ids = sections.find((s) => s.id === productPickerForSection)
                      ?.config?.productIds || [];
                    const checked = ids.includes(p.id);
                    return (
                      <li key={p.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggleProductInSection(productPickerForSection, p.id)
                          }
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-900">{p.name}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterBuilder;
